import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/propertyMonthlyInspections.module.css';
import propertyMonthlyInspectionsService from '../services/propertyMonthlyInspectionsService';
import { useAuth } from '../contexts/AuthContext';

const PropertyMonthlyInspections = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isGuardianManager } = useAuth();
  
  // State management
  const [property, setProperty] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ignoredMonths, setIgnoredMonths] = useState({});


  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Real API calls - the monthly inspections endpoint returns both property and months data
        const monthsResponse = await propertyMonthlyInspectionsService.getMonthlyInspections(id);
        
        console.log('🔍 [FRONTEND] Monthly inspections API response:', monthsResponse.data);
        console.log('🔍 [FRONTEND] Full months data:', JSON.stringify(monthsResponse.data.data, null, 2));
        // Check specifically for November 2025
        const nov2025 = monthsResponse.data.data?.find(m => m.month === 'November' && m.year === '2025');
        console.log('🔍 [FRONTEND] November 2025 data:', nov2025);
        
        // Extract property and months data from the combined response
        if (monthsResponse.data.property) {
          setProperty(monthsResponse.data.property);
        }
        
        if (monthsResponse.data.data) {
          setMonths(monthsResponse.data.data);
          
          // Initialize ignored months state
          // Laravel returns ignored as 1 or 0, not boolean
          const ignored = {};
          monthsResponse.data.data.forEach(month => {
            // Check for both boolean true and numeric 1 (Laravel format)
            if (month.ignored === 1 || month.ignored === true) {
              const key = `${month.year}-${month.monthNumber}`;
              ignored[key] = true;
            }
          });
          console.log('🔍 [FRONTEND] Initialized ignored months:', ignored);
          setIgnoredMonths(ignored);
        } else {
          // Fallback: if data is directly in monthsResponse.data (old format)
          setMonths(monthsResponse.data);
          
          const ignored = {};
          monthsResponse.data.forEach(month => {
            // Check for both boolean true and numeric 1 (Laravel format)
            if (month.ignored === 1 || month.ignored === true) {
              const key = `${month.year}-${month.monthNumber}`;
              ignored[key] = true;
            }
          });
          console.log('🔍 [FRONTEND] Initialized ignored months (fallback):', ignored);
          setIgnoredMonths(ignored);
        }
      } catch (error) {
        console.error('❌ [FRONTEND] Error loading monthly inspections data:', error);
        toast.error('Failed to load monthly inspections data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Handle ignore checkbox change
  const handleIgnoreChange = async (year, month, monthNumber, checked) => {
    const key = `${year}-${monthNumber}`;
    
    if (checked) {
      setIgnoredMonths(prev => ({ ...prev, [key]: true }));
    } else {
      setIgnoredMonths(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }

    try {
      // Always try API call first, regardless of mock data mode
      console.log(`🔄 [FRONTEND] Updating ignore status: ${month} ${year} = ${checked}`);
      const response = await propertyMonthlyInspectionsService.toggleIgnore(id, year, month, monthNumber, checked);
      
      if (response.data.success) {
        toast.success(response.data.message);
        console.log(`✅ [FRONTEND] Ignore status updated from ${response.data.source} source`);
      } else {
        throw new Error(response.data.message || 'Failed to update ignore status');
      }
    } catch (error) {
      console.error('Error toggling ignore:', error);
      toast.error(`Failed to update ignore status for ${month} ${year}`);
      
      // Revert the checkbox state on API error
      if (checked) {
        setIgnoredMonths(prev => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      } else {
        setIgnoredMonths(prev => ({ ...prev, [key]: true }));
      }
      
      // Laravel-style error handling
      if (error.response && error.response.status === 422) {
        // Validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        toast.error(`Validation Error: ${errorMessages}`);
      } else if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update ignore status. Please try again.');
      }
    }
  };

  // Get row class based on status
  const getRowClass = (month) => {
    if (month.ignored) {
      return '';
    }
    
    // Check if report exists (object) or is null/undefined/None
    const hasReport = month.report && typeof month.report === 'object' && month.report.id;
    const hasInspection = month.inspection && month.inspection !== 'None';
    
    if (!hasReport && !hasInspection) {
      return styles.danger;
    }
    
    // Check report status_id: 1 = Published, 2 = Draft (unpublished)
    if (hasReport && month.report.status_id === 2) { // unpublished/draft
      return styles.warning;
    }
    
    if (hasReport && month.report.status_id === 1) { // published
      return styles.success;
    }
    
    return '';
  };

  // Get report cell class
  const getReportCellClass = (month) => {
    if (month.ignored) {
      return '';
    }
    
    // Check if report exists (object) or is null/undefined/None
    const hasReport = month.report && typeof month.report === 'object' && month.report.id;
    
    if (!hasReport) {
      return styles.danger;
    }
    
    // Check report status_id: 1 = Published, 2 = Draft (unpublished)
    if (month.report.status_id === 2) { // unpublished/draft
      return styles.warning;
    }
    
    if (month.report.status_id === 1) { // published
      return styles.success;
    }
    
    return '';
  };

  // Get inspection cell class
  const getInspectionCellClass = (month) => {
    if (!month.ignored) {
      if (!month.inspection) {
        return styles.danger;
      } else {
        return styles.success;
      }
    }
    return '';
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Dashboard', path: '/' },
    { name: 'Properties', path: '/properties' },
    { name: property?.name || 'Property', path: `/properties/${id}` },
    { name: 'Monthly Inspections', path: `/properties/${id}/monthly-inspections` }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          Loading monthly inspections...
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} container`}>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <a
                href={crumb.path}
                className={styles.breadcrumbItem}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
              >
                {crumb.name}
              </a>
            )}
          </span>
        ))}
      </div>

      {/* Main Panel */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panel} panel-default panel-form`}>
            <div className={styles.panelHeading}>
              <h3 className={styles.panelTitle}>Manage Monthly Inspections</h3>
              <div className={styles.panelActions}>
                {!isGuardianManager() && (
                  <button
                    className={styles.createButton}
                    onClick={() => navigate(`/properties/${id}/reports/monthly_inspections/create`)}
                  >
                    Create New Report
                  </button>
                )}
              </div>
            </div>
            <div className={styles.panelBody}>
              {/* Property Overview */}
              <dl className={`${styles.propertyOverview} list green-list green-list-with-width`}>
                <div className={styles.propertyOverviewItem}>
                  <dt>Property ID:</dt>
                  <dd>{property?.property_id}</dd>
                </div>
                <div className={styles.propertyOverviewItem}>
                  <dt>Property Name:</dt>
                  <dd>{property?.name}</dd>
                </div>
                <div className={styles.propertyOverviewItem}>
                  <dt>Property Status:</dt>
                  <dd>{property?.statusText}</dd>
                </div>
                <div className={styles.propertyOverviewItem}>
                  <dt>Property address:</dt>
                  <dd>{property?.address_1}</dd>
                </div>
                <div className={styles.propertyOverviewItem}>
                  <dt>Postcode:</dt>
                  <dd>{property?.postcode}</dd>
                </div>
                {property?.date_taken_on && (
                  <div className={styles.propertyOverviewItem}>
                    <dt>Lease dates:</dt>
                    <dd>
                      {new Date(property.date_taken_on).toLocaleDateString('en-GB')}
                      {property.date_taken_back && (
                        <> to {new Date(property.date_taken_back).toLocaleDateString('en-GB')}</>
                      )}
                    </dd>
                  </div>
                )}
                {property?.date_notice && (
                  <div className={styles.propertyOverviewItem}>
                    <dt>Notice date:</dt>
                    <dd>{new Date(property.date_notice).toLocaleDateString('en-GB')}</dd>
                  </div>
                )}
              </dl>

              {/* Monthly Inspections Table */}
              <table className={`${styles.table} table-hover table-condensed`}>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Year</th>
                    <th>Report</th>
                    <th>Inspection</th>
                    <th>Ignore</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => (
                    <tr key={index} className={getRowClass(month)}>
                      <td>{month.month}</td>
                      <td>{month.year}</td>
                      <td className={getReportCellClass(month)}>
                        {month.report && typeof month.report === 'object' && month.report.id ? (
                          <button 
                            type="button"
                            className={`${styles.reportLink} hidden-print-link`}
                            onClick={() => {
                              // Laravel route: /properties/{property}/reports/monthly_inspections/{reportId}/edit/{period?}
                              // React route: /properties/:propertyId/reports/monthly_inspections/:reportId/edit
                              navigate(`/properties/${property.id}/reports/monthly_inspections/${month.report.id}/edit`);
                            }}
                          >
                            {month.report.monthName ? month.report.monthName() : month.month} ({month.report.year || month.year}) - {month.report.status_id === 1 ? 'Published' : 'Draft'}
                          </button>
                        ) : (
                          'None'
                        )}
                      </td>
                      <td className={getInspectionCellClass(month)}>
                        {month.inspection ? (typeof month.inspection === 'object' ? month.inspection.toString() : month.inspection) : 'None'}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={ignoredMonths[`${month.year}-${month.monthNumber}`] || false}
                          onChange={(e) => handleIgnoreChange(month.year, month.month, month.monthNumber, e.target.checked)}
                          className={`${styles.ignoreCheckbox} js-ignore-monthly-inspection-checkbox`}
                          data-month={month.monthNumber}
                          data-year={month.year}
                          data-property-id={property?.id}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyMonthlyInspections; 